if (!customElements.get('custom-bundle-select')) {
  customElements.define(
    'custom-bundle-select',
    class CustomBundleSelect extends HTMLElement {
      connectedCallback() {
        this.select = this.querySelector('[data-bundle-variant-select]');
        this.trigger = this.querySelector('[data-bundle-variant-trigger]');
        this.menu = this.querySelector('[data-bundle-variant-menu]');
        this.label = this.querySelector('[data-bundle-variant-label]');

        this.onClick = this.onClick.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

        this.addEventListener('click', this.onClick);
        this.select?.addEventListener('change', this.onSelectChange);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onDocumentKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.select?.removeEventListener('change', this.onSelectChange);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      get isOpen() {
        return this.classList.contains('is-open');
      }

      onClick(event) {
        const option = event.target.closest('[data-bundle-variant-option]');
        if (option && this.contains(option)) {
          this.selectOption(option);
          return;
        }

        if (event.target.closest('[data-bundle-variant-trigger]')) {
          this.toggle();
        }
      }

      onSelectChange() {
        this.syncUI();
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) {
          this.close();
        }
      }

      onDocumentKeydown(event) {
        if (event.key === 'Escape') {
          this.close();
        }
      }

      toggle() {
        if (this.isOpen) {
          this.close();
          return;
        }

        this.open();
      }

      open() {
        this.closest('custom-bundle')?.querySelectorAll('custom-bundle-select').forEach((selectEl) => {
            if (selectEl !== this) selectEl.close();
          });

        if (this.isOpen) return;

        this.classList.add('is-open');
        this.closest('.custom-bundle__card')?.classList.add('is-dropdown-open');
        this.trigger?.setAttribute('aria-expanded', 'true');
        if (this.menu) this.menu.hidden = false;
      }

      close() {
        if (!this.isOpen) return;

        this.classList.remove('is-open');
        this.closest('.custom-bundle__card')?.classList.remove('is-dropdown-open');
        this.trigger?.setAttribute('aria-expanded', 'false');
        if (this.menu) this.menu.hidden = true;
      }

      selectOption(optionEl) {
        if (!this.select || optionEl.disabled) return;

        this.select.value = optionEl.dataset.value || '';
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
        this.close();
      }

      syncUI() {
        const placeholder = this.dataset.placeholder || '';
        const value = this.select?.value || '';
        const hasValue = Boolean(value);
        const selectedOption = this.select?.selectedOptions?.[0];

        this.classList.toggle('is-empty', !hasValue);

        if (this.label) {
          this.label.textContent = hasValue ? selectedOption?.text?.trim() || value : placeholder;
        }

        this.querySelectorAll('[data-bundle-variant-option]').forEach((optionEl) => {
          optionEl.setAttribute('aria-selected', String(optionEl.dataset.value === value));
        });
      }
    }
  );
}

if (!customElements.get('custom-bundle')) {
  customElements.define(
    'custom-bundle',
    class CustomBundle extends HTMLElement {
      connectedCallback() {
        this.min = Number(this.dataset.min) || 1;
        this.max = Number(this.dataset.max) || 4;
        this.discounts = {
          1: Number(this.dataset['discount-1']) || 0,
          2: Number(this.dataset['discount-2']) || 0,
          3: Number(this.dataset['discount-3']) || 0,
          4: Number(this.dataset['discount-4']) || 0,
        };
        this.discountName = this.dataset.discountName || '';
        this.discountType = this.dataset.discountType === 'Fixed' ? 'Fixed' : 'Percentages';
        this.currencyRate = Number(window.Shopify?.currency?.rate) || 1;
        this.productsUrl = this.dataset.productsUrl || '';
        this.minFromProducts = this.dataset.minFromProducts === 'true';
        this.randomProducts = this.dataset.randomProducts === 'true';
        this.hasParent = Boolean(this.querySelector('[data-bundle-parent]'));
        this.parentPrice = Number(this.dataset.parentPrice) || 0;
        this.moneyFormat = this.dataset.moneyFormat || window.theme?.settings?.money_with_currency_format || '{{amount}}';
        this.savingsTemplate = this.dataset.savingsTemplate || '';
        this.savingsFixedTemplate = this.dataset.savingsFixedTemplate || '';
        this.labelAdd = this.dataset.labelAdd || '';
        this.labelMin = this.dataset.labelMin || '';
        this.labelMax = this.dataset.labelMax || '';

        this.cards = this.querySelectorAll('.custom-bundle__card');
        this.grid = this.querySelector('[data-bundle-grid]');
        this.totalEl = this.querySelector('[data-bundle-total]');
        this.compareEl = this.querySelector('[data-bundle-compare]');
        this.savingsEl = this.querySelector('[data-bundle-savings]');
        this.selectedCountEl = this.querySelector('[data-bundle-selected-count]');
        this.totalCountEl = this.querySelector(
          '.custom-bundle__count span:not([data-bundle-selected-count])'
        );
        this.headingEl = this.querySelector('[data-bundle-heading]');
        this.badgeEl = this.querySelector('[data-bundle-badge]');
        this.introEl = this.querySelector('[data-bundle-intro]');
        this.addButton = this.querySelector('[data-bundle-add]');
        this.addButtonLabel = this.addButton?.querySelector('[data-bundle-add-label]');
        this.spinner = this.addButton?.querySelector('.loading__spinner');
        this.cart = document.querySelector('cart-drawer');
        this.isLoading = false;

        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onAddButtonClick = this.onAddButtonClick.bind(this);
        this.onCampaignCartUpdate = this.scheduleCampaignCartRefresh.bind(this);

        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        this.addButton?.addEventListener('click', this.onAddButtonClick);
        document.addEventListener('cartUpdated', this.onCampaignCartUpdate);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onParentVariantChange.bind(this)
          );
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.onCampaignCartUpdate
          );
        }

        if (this.productsUrl) {
          this.loadFilteredProducts();
        } else if (this.randomProducts) {
          this.loadRandomStaticProducts();
        } else {
          this.updateOfferCopy(this.getAddonCards().length);
          this.hidden = false;
          this.updateTotals();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onClick);
        this.addButton?.removeEventListener('click', this.onAddButtonClick);
        document.removeEventListener('cartUpdated', this.onCampaignCartUpdate);
        window.clearTimeout(this.campaignCartRefreshTimer);

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        this.productsAbortController?.abort();
      }

      getFilteredProductsUrl() {
        const sourceUrl = new URL(this.productsUrl, window.location.origin);
        const requestUrl = new URL(`${sourceUrl.pathname}${sourceUrl.search}`, window.location.origin);

        if (!requestUrl.pathname.includes('/collections/')) {
          throw new Error('Bundle products URL must point to a collection.');
        }

        requestUrl.searchParams.set('view', this.randomProducts ? 'bundle-random' : 'bundle');
        requestUrl.searchParams.delete('page');

        return requestUrl;
      }

      async loadFilteredProducts() {
        this.productsAbortController?.abort();
        this.productsAbortController = new AbortController();
        this.setAttribute('aria-busy', 'true');

        try {
          const requestUrl = this.getFilteredProductsUrl();
          let responseDocument = await this.fetchProductsDocument(requestUrl);
          let cards = this.getCandidateCards(responseDocument);

          if (this.randomProducts) {
            const result = responseDocument.querySelector('[data-bundle-product-results]');
            const pages = Number(result?.dataset.pages) || 1;

            if (pages > 1) {
              const randomPage = Math.floor(Math.random() * pages) + 1;
              if (randomPage > 1) {
                const randomPageUrl = new URL(requestUrl);
                randomPageUrl.searchParams.set('page', String(randomPage));
                const randomPageDocument = await this.fetchProductsDocument(randomPageUrl);
                const randomPageCards = this.getCandidateCards(randomPageDocument);
                if (randomPageCards.length) cards = randomPageCards;
              }
            }
          }

          cards = this.selectProductCards(cards);

          if (!cards.length) {
            throw new Error('No available bundle products matched the filtered collection URL.');
          }

          this.replaceAddonCards(cards);
          this.refreshCards();
          this.finalizeLoadedProducts();
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Error loading filtered bundle products:', error);
          }
        } finally {
          this.removeAttribute('aria-busy');
        }
      }

      async fetchProductsDocument(url) {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: this.productsAbortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Unable to load bundle products: ${response.status}`);
        }

        return new DOMParser().parseFromString(await response.text(), 'text/html');
      }

      getCandidateCards(responseDocument) {
        const parentProductId = this.dataset.parentProductId || '';
        const productIds = new Set();

        return [...responseDocument.querySelectorAll('.custom-bundle__card')].filter((card) => {
          const productId = card.dataset.productId || '';
          if (!productId || productId === parentProductId || productIds.has(productId)) return false;

          productIds.add(productId);
          return true;
        });
      }

      selectProductCards(cards) {
        const selectedCards = [...cards];

        if (this.randomProducts) {
          for (let index = selectedCards.length - 1; index > 0; index -= 1) {
            const randomIndex = Math.floor(Math.random() * (index + 1));
            [selectedCards[index], selectedCards[randomIndex]] = [
              selectedCards[randomIndex],
              selectedCards[index],
            ];
          }
        }

        return selectedCards.slice(0, 4);
      }

      loadRandomStaticProducts() {
        try {
          const candidateCards = this.getAddonCards();
          const selectedCards = new Set(this.selectProductCards(candidateCards));
          candidateCards.forEach((card) => {
            if (!selectedCards.has(card)) card.remove();
          });

          this.refreshCards();
          this.finalizeLoadedProducts();
        } catch (error) {
          console.error('Error selecting random bundle products:', error);
        }
      }

      finalizeLoadedProducts() {
        const addonCards = this.getAddonCards();
        if (this.minFromProducts) this.min = addonCards.length;
        if (this.max < this.min) this.max = this.min;

        if (!addonCards.length || addonCards.length < this.min) {
          throw new Error('The bundle source does not contain enough available products.');
        }

        this.applyInitialSelection(addonCards);
        this.updateOfferCopy(addonCards.length);
        this.hidden = false;
        this.updateTotals();
      }

      replaceAddonCards(cards) {
        if (!this.grid) return;

        this.grid
          .querySelectorAll('.custom-bundle__card:not([data-bundle-parent])')
          .forEach((card) => card.remove());
        cards.forEach((card) => this.grid.appendChild(card));
      }

      refreshCards() {
        this.cards = this.querySelectorAll('.custom-bundle__card');
        this.hasParent = Boolean(this.querySelector('[data-bundle-parent]'));
      }

      getAddonCards() {
        return [...this.cards].filter((card) => !card.hasAttribute('data-bundle-parent'));
      }

      applyInitialSelection(addonCards) {
        addonCards.forEach((card, index) => {
          const checkbox = card.querySelector('[data-bundle-checkbox]');
          if (!checkbox) return;

          checkbox.checked = this.hasParent ? index < this.min : true;
          card.classList.toggle('is-selected', checkbox.checked);
        });
      }

      getMaxDiscountForCount(count) {
        for (let quantity = Math.min(count, 4); quantity >= 1; quantity -= 1) {
          if (this.discounts[quantity] > 0) return this.discounts[quantity];
        }

        return 0;
      }

      updateOfferCopy(addonCount, bundleDiscountEnabled = true) {
        const maxDiscount = bundleDiscountEnabled
          ? this.getMaxDiscountForCount(addonCount)
          : 0;
        const isFixed = this.discountType === 'Fixed';
        const formattedFixedDiscount = this.formatMoney(
          this.getFixedDiscountCents(maxDiscount)
        );

        if (this.headingEl) {
          this.headingEl.textContent = maxDiscount
            ? this.dataset.titleDiscount || ''
            : this.dataset.titleNoDiscount || '';
        }

        if (this.badgeEl) {
          this.badgeEl.hidden = maxDiscount <= 0;
          const template = isFixed
            ? this.dataset.maxDiscountFixedLabel || ''
            : this.dataset.maxDiscountLabel || '';
          this.badgeEl.textContent = template
            .replaceAll('__AMOUNT__', formattedFixedDiscount)
            .replaceAll('__PERCENTAGE__', String(maxDiscount));
        }

        if (this.introEl) {
          let template = this.dataset.introNoDiscount || '';
          if (maxDiscount) {
            template = isFixed
              ? this.dataset.introFixed || ''
              : this.dataset.introDiscount || '';
          }
          this.introEl.innerHTML = template
            .replaceAll('__AMOUNT__', formattedFixedDiscount)
            .replaceAll('__PERCENTAGE__', String(maxDiscount));
        }
      }

      onChange(event) {
        const { target } = event;

        if (target.matches('[data-bundle-checkbox]')) {
          const card = target.closest('.custom-bundle__card');
          card?.classList.toggle('is-selected', target.checked);
          if (!target.checked) this.hideVariantHint(card);
          this.updateTotals();
          return;
        }

        if (target.matches('[data-bundle-variant-select]')) {
          this.onVariantSelectChange(target);
        }
      }

      onClick(event) {
        const card = event.target.closest('.custom-bundle__card');
        if (!card || !this.contains(card) || card.hasAttribute('data-bundle-parent')) return;

        if (event.target.closest('a, custom-bundle-select, .custom-bundle__checkbox')) {
          return;
        }

        const checkbox = card.querySelector('[data-bundle-checkbox]');
        if (!checkbox || checkbox.disabled) return;

        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onVariantSelectChange(select) {
        const card = select.closest('.custom-bundle__card');
        if (!card) return;

        const selectedOption = select.selectedOptions[0];
        const variantId = selectedOption?.value || '';

        if (!variantId) {
          card.dataset.variantId = '';
          return;
        }

        this.hideVariantHint(card);

        const price = Number(selectedOption.dataset.price) || 0;
        const imageUrl = selectedOption.dataset.image || '';
        const previousPrice = Number(card.dataset.price) || 0;

        card.dataset.variantId = variantId;
        card.dataset.price = String(price);

        if (imageUrl) {
          const image = card.querySelector('[data-bundle-image]');
          if (image) image.src = imageUrl;
        }

        const priceEl = card.querySelector('[data-bundle-price]');
        if (priceEl) priceEl.innerHTML = this.formatMoney(price);

        const checkbox = card.querySelector('[data-bundle-checkbox]');
        if (checkbox && !checkbox.checked) {
          checkbox.checked = true;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        if (checkbox?.checked && price !== previousPrice) {
          this.updateTotals();
        }
      }

      onParentVariantChange(event) {
        const variant = event?.data?.variant;
        if (!variant || variant.price == null) return;

        this.parentPrice = Number(variant.price) || 0;
        this.updateParentCard(variant);
        this.updateTotals();
      }

      updateParentCard(variant) {
        const parentCard = this.querySelector('[data-bundle-parent]');
        if (!parentCard) return;

        parentCard.dataset.variantId = String(variant.id || '');
        parentCard.dataset.price = String(this.parentPrice);

        const priceEl = parentCard.querySelector('[data-bundle-price]');
        if (priceEl) priceEl.innerHTML = this.formatMoney(this.parentPrice);

        const imageUrl = variant.featured_image?.src || variant.featured_media?.preview_image?.src || '';
        if (!imageUrl) return;

        const image = parentCard.querySelector('[data-bundle-image]');
        if (image) image.src = imageUrl;
      }

      getSelectedCards() {
        return [...this.cards].filter((card) => {
          const checkbox = card.querySelector('[data-bundle-checkbox]');
          return Boolean(checkbox?.checked);
        });
      }

      getSelectedCardsMissingVariant() {
        return this.getSelectedCards().filter((card) => !card.dataset.variantId);
      }

      showVariantHint(card) {
        const hint = card?.querySelector('[data-bundle-variant-hint]');
        if (hint) hint.hidden = false;
      }

      hideVariantHint(card) {
        const hint = card?.querySelector('[data-bundle-variant-hint]');
        if (hint) hint.hidden = true;
      }

      showMissingVariantHints(missingCards) {
        missingCards.forEach((card) => this.showVariantHint(card));

        const firstCard = missingCards[0];
        this.scrollCardForVariantHint(firstCard);
        firstCard?.querySelector('custom-bundle-select')?.open();
      }

      isMobileViewport() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      getStickyOffset() {
        const styles = getComputedStyle(document.documentElement);

        return (
          parseFloat(styles.getPropertyValue('--sticky-offset')) ||
          parseFloat(styles.getPropertyValue('--header-height')) ||
          0
        );
      }

      isCardOrDropdownClipped(card) {
        const stickyTop = this.getStickyOffset();
        const viewportBottom = window.innerHeight;
        const cardRect = card.getBoundingClientRect();
        const cardVisible = cardRect.top >= stickyTop && cardRect.bottom <= viewportBottom;

        const select = card.querySelector('custom-bundle-select');
        const selectRect = select?.getBoundingClientRect();
        const rootRem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
        const dropdownBottom = (selectRect?.bottom ?? cardRect.bottom) + (19.8 + 2.4) * rootRem;
        const dropdownVisible = dropdownBottom <= viewportBottom && (selectRect?.top ?? cardRect.top) >= stickyTop;

        return !cardVisible || !dropdownVisible;
      }

      scrollCardForVariantHint(card) {
        if (!card) return;
        if (this.closest('.bundle') && !this.isMobileViewport()) return;
        if (!this.isMobileViewport() && !this.isCardOrDropdownClipped(card)) return;

        card.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      }

      getDiscountForCount(count) {
        return this.discounts[count] || 0;
      }

      getFixedDiscountCents(discount) {
        return Math.round(discount * this.currencyRate * 100);
      }

      getBundleCards(selectedCards = this.getSelectedCards()) {
        if (!this.hasParent) return selectedCards;

        const parentCard = this.querySelector('[data-bundle-parent]');
        return parentCard ? [parentCard, ...selectedCards] : selectedCards;
      }

      getCampaignInput(card) {
        return card?.querySelector?.('.dry-run-bundle-campaign-id') || null;
      }

      getCampaignTiers(input) {
        try {
          const tiers = JSON.parse(input?.dataset.campaignTiers || '[]');
          if (!Array.isArray(tiers)) return [];

          return tiers
            .map((tier) => ({
              threshold: Math.round(Number(tier.thresholdAmount ?? tier.threshold) * 100),
              percentage: Number(tier.percentage),
            }))
            .filter((tier) =>
              Number.isFinite(tier.threshold)
              && tier.threshold >= 0
              && Number.isFinite(tier.percentage)
              && tier.percentage > 0
            )
            .sort((a, b) => a.threshold - b.threshold);
        } catch {
          return [];
        }
      }

      getCampaignPricing(cards) {
        const campaignGroups = new Map();

        cards.forEach((card) => {
          const input = this.getCampaignInput(card);
          const campaignId = input?.value;
          if (!campaignId) return;

          if (!campaignGroups.has(campaignId)) {
            campaignGroups.set(campaignId, {
              subtotal: Number(input.dataset.campaignCartSubtotal) || 0,
              tiers: this.getCampaignTiers(input),
            });
          }

          const group = campaignGroups.get(campaignId);
          group.subtotal += Number(card.dataset.price) || 0;
        });

        const percentages = new Map();
        let maxPercentage = 0;

        campaignGroups.forEach((group, campaignId) => {
          const percentage = group.tiers.reduce(
            (activePercentage, tier) =>
              group.subtotal >= tier.threshold ? tier.percentage : activePercentage,
            0
          );
          percentages.set(campaignId, percentage);
          maxPercentage = Math.max(maxPercentage, percentage);
        });

        const total = cards.reduce((sum, card) => {
          const price = Number(card.dataset.price) || 0;
          const campaignId = this.getCampaignInput(card)?.value;
          const percentage = percentages.get(campaignId) || 0;
          return sum + Math.round((price * (100 - percentage)) / 100);
        }, 0);

        return {
          hasCampaign: campaignGroups.size > 0,
          maxPercentage,
          percentages,
          total,
        };
      }

      updateCardPrices(selectedBundleCards) {
        const selectedCards = new Set(selectedBundleCards);

        this.cards.forEach((card) => {
          const price = Number(card.dataset.price) || 0;
          const input = this.getCampaignInput(card);
          let displayPrice = price;

          if (input?.value) {
            const pricingCards = selectedCards.has(card)
              ? selectedBundleCards
              : [...selectedBundleCards, card];
            const pricing = this.getCampaignPricing(pricingCards);
            const percentage = pricing.percentages.get(input.value) || 0;
            displayPrice = Math.round((price * (100 - percentage)) / 100);
          }

          const priceEl = card.querySelector('[data-bundle-price]');
          if (priceEl) priceEl.innerHTML = this.formatMoney(displayPrice);
        });
      }

      scheduleCampaignCartRefresh() {
        window.clearTimeout(this.campaignCartRefreshTimer);
        this.campaignCartRefreshTimer = window.setTimeout(
          () => this.refreshCampaignCartSubtotals(),
          100
        );
      }

      async refreshCampaignCartSubtotals() {
        const campaignInputs = this.querySelectorAll('.dry-run-bundle-campaign-id');
        if (!campaignInputs.length) return;

        try {
          const response = await fetch(`${routes.cart_url}.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
          if (!response.ok) return;

          const cart = await response.json();
          const subtotals = new Map();

          cart.items.forEach((item) => {
            const campaignId = item.properties?._dry_run_campaign_id;
            if (!campaignId) return;
            subtotals.set(
              campaignId,
              (subtotals.get(campaignId) || 0) + (Number(item.original_line_price) || 0)
            );
          });

          campaignInputs.forEach((input) => {
            input.dataset.campaignCartSubtotal = String(subtotals.get(input.value) || 0);
          });
          this.updateTotals();
        } catch {
        }
      }

      updateTotals() {
        const selectedCards = this.getSelectedCards();
        const addonsCount = selectedCards.length;
        const selectedBundleCards = this.getBundleCards(selectedCards);
        const compare = selectedBundleCards.reduce(
          (sum, card) => sum + (Number(card.dataset.price) || 0),
          0
        );
        const campaignPricing = this.getCampaignPricing(selectedBundleCards);
        const bundleDiscountEnabled = !campaignPricing.hasCampaign;
        const discount = bundleDiscountEnabled
          ? this.getDiscountForCount(addonsCount)
          : campaignPricing.maxPercentage;
        let saved;
        let total;

        if (bundleDiscountEnabled) {
          saved = this.discountType === 'Fixed'
            ? Math.min(compare, this.getFixedDiscountCents(discount))
            : compare - Math.floor((compare * (100 - discount)) / 100);
          total = compare - saved;
        } else {
          total = campaignPricing.total;
          saved = compare - total;
        }

        this.updateOfferCopy(this.getAddonCards().length, bundleDiscountEnabled);
        this.updateCardPrices(selectedBundleCards);

        if (this.totalEl) {
          this.totalEl.innerHTML = this.formatMoney(total);
        }

        if (this.compareEl) {
          this.compareEl.innerHTML = this.formatMoney(compare);
          this.compareEl.hidden = saved <= 0;
        }

        if (this.savingsEl) {
          if (saved > 0 && this.savingsTemplate) {
            this.savingsEl.hidden = false;
            const template = bundleDiscountEnabled && this.discountType === 'Fixed'
              ? this.savingsFixedTemplate
              : this.savingsTemplate;
            this.savingsEl.innerHTML = template
              .replaceAll('__AMOUNT__', this.formatMoney(saved))
              .replaceAll('__PERCENTAGE__', String(discount));
          } else {
            this.savingsEl.hidden = true;
          }
        }

        this.updateAddButton(addonsCount);
        this.updateSelectionCount(addonsCount);
      }

      updateSelectionCount(addonsCount) {
        if (!this.selectedCountEl) return;

        const count = this.hasParent ? addonsCount + 1 : addonsCount;
        this.selectedCountEl.textContent = String(count);

        if (this.totalCountEl) {
          const totalCount = this.getAddonCards().length + (this.hasParent ? 1 : 0);
          this.totalCountEl.textContent = String(totalCount);
        }
      }

      updateAddButton(addonsCount) {
        if (!this.addButton) return;

        const isBelowMin = addonsCount < this.min;
        const isAboveMax = addonsCount > this.max;
        const isValid = !isBelowMin && !isAboveMax && !this.isLoading;

        this.addButton.disabled = !isValid;
        this.addButton.setAttribute('aria-disabled', String(!isValid));

        if (isBelowMin) {
          this.setAddButtonLabel(this.labelMin.replaceAll('__COUNT__', String(this.min)));
        } else if (isAboveMax) {
          this.setAddButtonLabel(this.labelMax.replaceAll('__COUNT__', String(this.max)));
        } else {
          this.setAddButtonLabel(this.labelAdd);
        }
      }

      setAddButtonLabel(label) {
        if (this.addButtonLabel && label) this.addButtonLabel.textContent = label;
      }

      getParentVariantId(selectedCards = this.getSelectedCards()) {
        if (!this.hasParent) {
          return Number(selectedCards[0]?.dataset.variantId) || null;
        }

        const productInfo = this.closest('product-info');
        const variantInput = productInfo?.querySelector(
          'form[data-type="add-to-cart-form"] input[name="id"]'
        );

        return Number(variantInput?.value) || null;
      }

      createBundleId() {
        if (typeof window.crypto?.randomUUID === 'function') {
          return window.crypto.randomUUID();
        }

        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      getBundleDiscountProperties(bundleId, bundleDiscountEnabled = true) {
        const properties = { _bundle_id: bundleId };

        if (bundleDiscountEnabled) {
          properties._bundle_discount_name = this.discountName;
          properties._bundle_discount_type = this.discountType;
          properties._bundle_discount_percentages = JSON.stringify([
            this.discounts[1],
            this.discounts[2],
            this.discounts[3],
            this.discounts[4],
          ]);
        }

        return properties;
      }

      buildCartItems(parentVariantId, bundleId, selectedCards) {
        const parentCard = this.hasParent
          ? this.querySelector('[data-bundle-parent]')
          : selectedCards[0];
        const selectedBundleCards = this.getBundleCards(selectedCards);
        const hasCampaignProduct = selectedBundleCards.some(
          (card) => Boolean(this.getCampaignInput(card)?.value)
        );
        const parentProperties = this.getBundleDiscountProperties(
          bundleId,
          !hasCampaignProduct
        );
        const materialPropertyKey = this.dataset.materialPropertyKey;
        const parentTitleSubtitle = parentCard?.dataset?.titleSubtitle;

        const parentCampaignId = this.getCampaignInput(parentCard)?.value;
        if (parentCampaignId) {
          parentProperties._dry_run_campaign_id = parentCampaignId;
        }

        if (materialPropertyKey && parentTitleSubtitle) {
          parentProperties[materialPropertyKey] = parentTitleSubtitle;
        }

        const parentItem = {
          id: parentVariantId,
          quantity: 1,
          properties: parentProperties,
        };

        const addonCards = this.hasParent ? selectedCards : selectedCards.slice(1);
        const addonItems = addonCards.map((card) => {
          const properties = {
            _bundle_id: bundleId,
          };

          if (materialPropertyKey && card.dataset.titleSubtitle) {
            properties[materialPropertyKey] = card.dataset.titleSubtitle;
          }

          const campaignId = this.getCampaignInput(card)?.value;
          if (campaignId) {
            properties._dry_run_campaign_id = campaignId;
          }

          return {
            id: Number(card.dataset.variantId),
            quantity: 1,
            parent_id: String(parentVariantId),
            properties,
          };
        });

        return [parentItem, ...addonItems];
      }

      async prepareCart() {
        this.cart = document.querySelector('cart-drawer');
        if (!this.cart) return;

        if (typeof this.cart.getSectionsToRender !== 'function') {
          let timeout;
          try {
            await Promise.race([
              window.themeAssets?.loadGroup('cart'),
              new Promise((_, reject) => {
                timeout = setTimeout(() => reject(new Error('Cart initialization timed out')), 15000);
              }),
            ]);
          } finally {
            clearTimeout(timeout);
          }
          this.cart = document.querySelector('cart-drawer');
        }

        if (typeof this.cart?.getSectionsToRender !== 'function'
          || typeof this.cart?.setActiveElement !== 'function'
          || typeof this.cart?.renderContents !== 'function') {
          throw new Error('Cart drawer failed to initialize');
        }
      }

      showCartError(message = '') {
        let errorElement = this.querySelector('[data-bundle-error]');
        if (!errorElement && message) {
          errorElement = document.createElement('p');
          errorElement.setAttribute('data-bundle-error', '');
          errorElement.setAttribute('role', 'alert');
          this.addButton.insertAdjacentElement('afterend', errorElement);
        }
        if (errorElement) {
          errorElement.textContent = message;
          errorElement.hidden = !message;
        }
      }

      async onAddButtonClick(event) {
        if (this.isLoading || this.addButton?.disabled) return;

        const selectedCards = this.getSelectedCards();
        const addonsCount = selectedCards.length;
        if (addonsCount < this.min || addonsCount > this.max) {
          this.updateAddButton(addonsCount);
          return;
        }

        const missingCards = this.getSelectedCardsMissingVariant();
        if (missingCards.length) {
          event.stopPropagation();
          this.showMissingVariantHints(missingCards);
          return;
        }

        const parentVariantId = this.getParentVariantId(selectedCards);
        if (!parentVariantId) return;

        const bundleId = this.createBundleId();
        const body = {
          items: this.buildCartItems(parentVariantId, bundleId, selectedCards),
        };

        this.setLoading(true);

        try {
          this.showCartError();
          await this.prepareCart();
          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(this.addButton);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(body);

          const response = await fetch(routes.cart_add_url, config);
          const cartData = await response.json();

          if (!response.ok || cartData.status) {
            this.showCartError(cartData.description || window.cartStrings?.error || window.theme?.strings?.cartError);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'custom-bundle',
              productVariantId: parentVariantId,
              errors: cartData.errors || cartData.description,
              message: cartData.message,
            });
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-bundle',
            productVariantId: parentVariantId,
            cartData,
          });

          if (this.cart) {
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(cartData);
          } else {
            window.location = window.routes.cart_url;
          }
        } catch (error) {
          console.error('Error adding custom bundle to cart:', error);
          this.showCartError(window.cartStrings?.error || window.theme?.strings?.cartError);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        this.isLoading = isLoading;
        this.addButton?.classList.toggle('loading', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
        this.updateAddButton(this.getSelectedCards().length);
      }

      formatMoney(cents) {
        if (typeof formatMoney === 'function') {
          return formatMoney(cents, this.moneyFormat);
        }

        return String(cents);
      }
    }
  );
}
